import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

export const fetchTables = createAsyncThunk(
  'tables/fetchTables',
  async (restaurantId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.tables.lastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      if (lastFetched && cacheAge < 5 * 60 * 1000) {
        return state.tables.items;
      }
      
      const response = await axios.get(API_ENDPOINTS.TABLES(restaurantId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tables');
    }
  }
);

export const addTable = createAsyncThunk(
  'tables/addTable',
  async (tableData, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.TABLES_ADD, tableData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add table');
    }
  }
);

export const updateTableStatus = createAsyncThunk(
  'tables/updateTableStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await axios.put(API_ENDPOINTS.TABLE_STATUS(id), { status });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update table status');
    }
  }
);

export const updateTableName = createAsyncThunk(
  'tables/updateTableName',
  async ({ id, tableNumber }, { rejectWithValue }) => {
    try {
      const response = await axios.put(API_ENDPOINTS.TABLE_DELETE(id), { tableNumber });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update table name');
    }
  }
);

export const deleteTable = createAsyncThunk(
  'tables/deleteTable',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(API_ENDPOINTS.TABLE_DELETE(id));
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete table');
    }
  }
);

const initialState = {
  items: [],
  loading: false,
  error: null,
  lastFetched: null,
};

const tableSlice = createSlice({
  name: 'tables',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    invalidateCache: (state) => {
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTables.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTables.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchTables.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addTable.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.lastFetched = Date.now();
      })
      .addCase(updateTableStatus.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateTableName.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(deleteTable.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item._id !== action.payload);
        state.lastFetched = Date.now();
      });
  },
});

export const selectAllTables = (state) => state.tables.items;
export const selectTableLoading = (state) => state.tables.loading;
export const selectTableError = (state) => state.tables.error;
export const selectAvailableTables = (state) => 
  state.tables.items.filter(table => table.status === 'Available');
export const selectOccupiedTables = (state) => 
  state.tables.items.filter(table => table.status === 'Occupied');

export const { clearError, invalidateCache } = tableSlice.actions;
export default tableSlice.reducer;

